// ─────────────────────────────────────────────────────────────────────────────
// PriceObservationService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for the pricing observations.
// Endpoints: /ai/kb/pricing-observations[...].
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, interval, of } from 'rxjs';
import { first, map, startWith, switchMap, timeout } from 'rxjs/operators';

import { APIResponse } from 'src/app/core/models/api-response';
import { IPageCount } from 'src/app/shared/models/page.model';
import { environment } from 'src/environments/environment';
import {
  IPriceColumnMapping,
  IPriceImport,
  IPriceImportInspection,
  IPriceImportMappingAsk,
  IPriceImportReceipt,
  IPriceImportScopeSelection,
  PRICE_IMPORT_PENDING_STATES,
  IPriceObservation,
  IPriceObservationFilters,
  IPriceObservationPreview,
  IUpdatePriceObservationPayload,
} from '../models/price-observation.models';

/// How often the journal is re-asked while an extraction runs, and for how
/// long. 1.5 s puts a spreadsheet on the first or second try, and 40 tries
/// cover the minute a full e-mail export can take -- after which the screen is
/// handed back rather than spinning for ever.
export const POLL_INTERVAL_MS = 1500;
export const POLL_ATTEMPTS = 40;
/// One upload is capped at 20 files server-side, so the newest page holds
/// every row this call could have created.
const POLL_PAGE_SIZE = 25;

@Injectable({
  providedIn: 'root',
})
export class PriceObservationService {
  private readonly baseUrl = `${environment.BASE_URL_API}ai/kb/pricing-observations`;

  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  public listObservations(
    page: IPageCount,
    key?: string,
    filters?: Partial<IPriceObservationFilters>,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<APIResponse<IPriceObservation>> {
    let params = new HttpParams().set('page', page.number).set('limit', page.size);

    if (key) params = params.set('key', key);
    if (sortBy) params = params.set('sort_by', sortBy);
    if (sortMode) params = params.set('sort_mode', sortMode);
    params = this.appendFilterParams(params, filters);

    return this.http.get<APIResponse<IPriceObservation>>(this.baseUrl, { params });
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  public updateObservation(id: number, payload: IUpdatePriceObservationPayload): Observable<APIResponse<IPriceObservation>> {
    return this.http.patch<APIResponse<IPriceObservation>>(`${this.baseUrl}/${id}`, payload);
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  public deleteObservation(id: number): Observable<APIResponse<IPriceObservation>> {
    return this.http.delete<APIResponse<IPriceObservation>>(`${this.baseUrl}/${id}`);
  }

  public bulkDeleteObservations(ids: number[]): Observable<APIResponse<number>> {
    return this.http.post<APIResponse<number>>(`${this.baseUrl}/bulk/delete`, { ids });
  }

  // ── The resolution loop ────────────────────────────────────────────────────
  // Resolving the customer, the product and the scope travels in the PATCH
  // payload of `updateObservation` — the server re-teaches the matcher on the
  // way. The one dedicated gesture is settling a conflict:

  /** Keep this row's price; the rival lands 'superseded'. */
  public keepPrice(id: number): Observable<APIResponse<IPriceObservation>> {
    return this.http.post<APIResponse<IPriceObservation>>(`${this.baseUrl}/${id}/keep-price`, {});
  }

  // ── Import ─────────────────────────────────────────────────────────────────

  /**
   * Upload 1..N price sources.
   *
   * With `dryRun` the response is a sampled preview and nothing is written —
   * not even a journal row. Without it the call returns as soon as the files
   * are queued (HTTP 202): the extraction is deferred, so the observations
   * appear afterwards and the import journal carries the report.
   *
   * @param formData one or more files under the field name 'ufile', plus the
   *                 scope fields when the batch is not an email export.
   */
  public importPriceSources(
    formData: FormData,
    dryRun = false,
  ): Observable<{ object?: IPriceObservationPreview; data?: IPriceImportReceipt[] }> {
    const url = dryRun ? `${this.baseUrl}/import?dry_run=1` : `${this.baseUrl}/import`;
    // `data` carries the journal's report shape (`import_id`), not the
    // serialised record the list route answers with (`id`).
    return this.http.post<{ object?: IPriceObservationPreview; data?: IPriceImportReceipt[] }>(url, formData);
  }

  /**
   * Wait for the uploaded imports to finish extracting, then answer.
   *
   * The upload route answers 202: it writes the journal rows, stores the
   * payloads and triggers the cron, then hands the operator back. Reloading the
   * observations at that instant would show the state before the extraction.
   *
   * Polling, and not a push: the extraction is a cron, there is no channel to
   * subscribe to. The journal is re-asked until none of the ids is still
   * `pending`/`running`, and gives up after `POLL_ATTEMPTS` so a stuck import
   * releases the screen — the journal still shows the row and its state.
   */
  public awaitImports(importIds: number[]): Observable<IPriceImport[]> {
    if (!importIds.length) return of([]);
    const wanted = new Set(importIds);
    return interval(POLL_INTERVAL_MS).pipe(
      startWith(0),
      switchMap(() => this.listImports({ number: 1, size: POLL_PAGE_SIZE })),
      map((response) => (response.data ?? []).filter((row) => wanted.has(row.id))),
      // Not found yet is not "finished": a row this call created must appear.
      first(
        (rows) =>
          rows.length === wanted.size &&
          rows.every((row) => !PRICE_IMPORT_PENDING_STATES.includes(row.state)),
      ),
      timeout({ each: POLL_INTERVAL_MS * POLL_ATTEMPTS, with: () => of([] as IPriceImport[]) }),
    );
  }

  /** The import journal — one row per uploaded file, with its report. */
  public listImports(
    page: IPageCount,
    state?: string,
    filters?: { source_kind?: string | null; scope?: string | null },
  ): Observable<APIResponse<IPriceImport>> {
    let params = new HttpParams().set('page', page.number).set('limit', page.size);
    if (state) params = params.set('state', state);
    if (filters?.source_kind) params = params.set('source_kind', filters.source_kind);
    if (filters?.scope) params = params.set('scope', filters.scope);
    return this.http.get<APIResponse<IPriceImport>>(`${this.baseUrl}/imports`, { params });
  }


  /**
   * Export the current selection, not the whole table.
   *
   * The filters the list is showing are sent as-is, and the backend reuses the
   * function the list filters with. An export that re-derived its own filter
   * would be a second answer to "which rows are Expired".
   */
  public exportObservations(
    key?: string,
    filters?: Partial<IPriceObservationFilters>,
  ): Observable<Blob> {
    let params = new HttpParams();
    if (key) params = params.set('key', key);
    params = this.appendFilterParams(params, filters);
    return this.http.get(`${this.baseUrl}/export`, { params, responseType: 'blob' });
  }

  /**
   * The drop-step answer: is this file importable as dropped? Nothing is
   * written — an operator who closes the dialog leaves no trace. The server
   * names the missing/expected columns; it is the only owner of that list.
   */
  public inspectPriceFile(file: File): Observable<{ object: IPriceImportInspection }> {
    const formData = new FormData();
    formData.append('files', file, file.name);
    return this.http.post<{ object: IPriceImportInspection }>(
      `${this.baseUrl}/import/inspect`,
      formData,
    );
  }

  /**
   * What an import awaiting a mapping is asking about, or an empty list.
   *
   * The attachment path of the same question the drop step asks: a message
   * carried a tariff whose columns are unnamed. It comes later because a
   * message is read by the cron, long after the upload that carried it.
   */
  public importMappingQuestion(importId: number): Observable<{ object: IPriceImportMappingAsk }> {
    return this.http.get<{ object: IPriceImportMappingAsk }>(
      `${this.baseUrl}/imports/${importId}/mapping`,
    );
  }

  /**
   * Answer it, for one attachment. The server reads that attachment again and
   * nothing else: the body's lines were created on the first pass, and its
   * siblings have their own layouts and their own questions.
   *
   * `attachmentId` is required by the route: a mapping standing for every
   * document a message carried would read one file's columns with another
   * file's answer, and the remaining questions would never be asked.
   */
  public applyImportMapping(
    importId: number,
    attachmentId: number,
    mapping: IPriceColumnMapping,
  ): Observable<{ object: IPriceImportReceipt }> {
    return this.http.post<{ object: IPriceImportReceipt }>(
      `${this.baseUrl}/imports/${importId}/mapping`,
      { attachment_id: attachmentId, mapping },
    );
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  /**
   * The batch audience, on the multipart body the import route reads.
   *
   * `file` appends nothing, and that is the contract: no scope means "the file
   * states its own audience", which is what attribution channel 1 reads off the
   * `customer` column. `scope=file` would be a fourth value the server does not
   * know.
   *
   * The named audiences carry their target with them — the server refuses a
   * `customer` scope with no customer rather than falling back on the file,
   * which would publish a tariff to whoever the rows happen to name.
   */
  public appendScope(formData: FormData, selection: IPriceImportScopeSelection): FormData {
    if (!selection.scope || selection.scope === 'file') return formData;
    formData.append('scope', selection.scope);
    if (selection.scope === 'customer') {
      (selection.customer_ids ?? []).forEach(
        (id) => formData.append('customer_ids', String(id)),
      );
    }
    if (selection.scope === 'group') {
      (selection.group_ids ?? []).forEach((id) => formData.append('group_ids', String(id)));
    }
    return formData;
  }

  /**
   * The confirmed column mapping, on the same multipart body as the file.
   *
   * One request, not two: the answer is confirmed before the upload — the drop
   * step's refusal opens the mapping screen, and confirming sends the file and
   * the reading together. A second route to confirm it afterwards would leave a
   * journal row in limbo between the two calls.
   */
  public appendMapping(formData: FormData, mapping: IPriceColumnMapping | null): FormData {
    if (!mapping) return formData;
    formData.append('mapping', JSON.stringify(mapping));
    return formData;
  }

  private appendFilterParams(params: HttpParams, filters?: Partial<IPriceObservationFilters>): HttpParams {
    if (!filters) return params;
    if (filters.import_id != null) params = params.set('import_id', filters.import_id);
    if (filters.customer_id != null) params = params.set('customer_id', filters.customer_id);
    if (filters.product_id != null) params = params.set('product_id', filters.product_id);
    if (filters.state) params = params.set('state', filters.state);
    if (filters.scope) params = params.set('scope', filters.scope);
    if (filters.is_current != null) params = params.set('is_current', filters.is_current ? '1' : '0');
    // The derived status: the backend translates it into a domain, so the
    // filter and the badge answer the same question.
    if (filters.status) params = params.set('status', filters.status);
    if (filters.source_kind) params = params.set('source_kind', filters.source_kind);
    if (filters.observed_from) params = params.set('observed_from', filters.observed_from);
    if (filters.observed_to) params = params.set('observed_to', filters.observed_to);
    return params;
  }
}
